import { define } from "cooky-cutter";
import {
  DecodedValueMap,
  QueryParamConfigMap,
  SetQuery,
} from "use-query-params";
import { faker } from "@faker-js/faker";

import { LocationSource } from "./trips/constants";

export type Point = {
  latitudeMicro: number;
  longitudeMicro: number;
};

export const pointFactory = define<Point>({
  latitudeMicro: faker.number.int() % 90000000,
  longitudeMicro: faker.number.int() % 180000000,
});

export const isValidLocation = (loc: Point | undefined | null) =>
  !!loc &&
  Number.isFinite(loc.latitudeMicro) &&
  Number.isFinite(loc.longitudeMicro);

export type PointAndRadius = {
  point: Point;
  radiusMeters: number;
};
export const pointAndRadiusFactory = define<PointAndRadius>({
  point: () => pointFactory(),
  radiusMeters: () => faker.number.int(),
});

export type Location = {
  accuracyCentiMeters: number;
  latlng: Point;
  locationTime: string;
  locationSource?: LocationSource;
  placeName?: string;
  placeAddress?: string;
  /** Associated place radius in meters (geofences with a co-located place). */
  placeRadiusMeters?: number;
  /** Geofence event-buffer radius in meters. */
  eventBufferMeters?: number;
};
export const locationFactory = define<Location>({
  accuracyCentiMeters: faker.number.int(),
  latlng: () => pointFactory(),
  locationTime: faker.date.recent().toISOString(),
  locationSource: LocationSource.LocationSourceUnspecified,
});

export type DwellLocation = Location & {
  startTime: string;
  endTime: string;
  dwellTimeMs: number;
  isUnexpected?: boolean;
  expectedDurationSeconds?: number;
};
export const dwellLocationFactory = define<DwellLocation>({
  startTime: faker.date.recent().toISOString(),
  endTime: faker.date.recent().toISOString(),
  accuracyCentiMeters: faker.number.int(),
  latlng: () => pointFactory(),
  locationTime: faker.date.recent().toISOString(),
  locationSource: LocationSource.LocationSourceUnspecified,
  dwellTimeMs: () => faker.number.int({ min: 1000, max: 86400000 }), // 1s–24h in ms, always valid for duration formatting
});

export type Place = {
  creationSource: string;
  creationTime: string;
  customerName: string;
  modificationTime: string;
  placeId: string;
  pointAndRadius: PointAndRadius;
};
export const placeFactory = define<Place>({
  creationSource: () => faker.string.sample(),
  creationTime: () => faker.string.sample(),
  customerName: () => faker.string.sample(),
  modificationTime: () => faker.string.sample(),
  placeId: () => faker.string.sample(),
  pointAndRadius: () => pointAndRadiusFactory(),
});

export type PlaceWithPadding = {
  place: Place;
  radiusMeters: number;
};
export const placeWithPaddingFactory = define<PlaceWithPadding>({
  place: placeFactory(),
  radiusMeters: faker.number.int(),
});

export type Category = {
  categoryId: string;
  name: string;
  stringValue?: string;
  timestampValue?: string;
  integerValue?: string;
  decimalValue?: string;
};
export const categoryFactory = define<Category>({
  categoryId: () => faker.string.sample(),
  name: () => faker.string.sample(),
});

export type CategoryKey = {
  categoryKeyId: string;
  creationTime?: string;
  dataType: string;
  deletionTime?: string;
  modificationTime?: string;
  name: string;
  orgId?: string;
  validUntilTime?: string;
};
export const categoryKeyFactory = define<CategoryKey>({
  categoryKeyId: () => faker.string.uuid(),
  name: () => faker.string.sample(),
  dataType: "EMPTY",
});

export interface QueryProps<T extends QueryParamConfigMap> {
  query: DecodedValueMap<T>;
  setQuery: SetQuery<T>;
}

export type MetricType =
  | "BATTERY"
  | "LIGHT"
  | "TEMPERATURE"
  | "STARTED_STOPPED_MOVING"
  | "PRESSURE"
  | "HUMIDITY";

export type Metric = {
  metricTime: string;
  batterySocPercent?: number;
  batteryVoltMilli?: number;
  lightNwCmSq?: number;
  lightLux?: number;
  pressureBarMilli?: number;
  startedStoppedMoving?: boolean;
  temperatureCelsiusMilli?: number;
  humidityPercentMilli?: number;
};

export const metricFactory = define<Metric>({
  metricTime: faker.date.recent().toISOString(),
});

export type FullQueryResult<T> = {
  data: T[];
  isLoading: boolean;
};
